import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Download, Pencil, Power, Upload } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { AgentsRegistry } from "../components/AgentsRegistry";
import { fetchAdminCompany, setAdminCompanyStatus, type CompanyProfile } from "../api/insurerApi";
import { fetchCoverageHistory, importCoverageList, type CoverageImport } from "../api/claimsApi";

const ghostClass =
  "inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent/20 hover:border-accent disabled:opacity-60";

/** Modèle de la liste de couverture : l'agent, puis ses ayants droit avec son matricule et leur lien. */
const COVERAGE_TEMPLATE = [
  "matricule;nom;date_naissance;sexe;telephone;lien",
  "AG-001;Mukendi Jean;1984-03-12;M;+243810000001;",
  "AG-001;Mukendi Esther;1988-07-02;F;;conjoint",
  "AG-001;Mukendi Divine;2023-05-20;F;;enfant",
].join("\r\n");

function downloadTemplate(): void {
  const blob = new Blob(["\ufeff" + COVERAGE_TEMPLATE + "\r\n"], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "modele-liste-couverture.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Admin : fiche d'une entreprise (comme celle d'un prestataire) avec son assureur actuel, l'historique de
 * ses rattachements et son portefeuille d'agents et d'ayants droit (import CSV ou saisie).
 */
export function CompanyDetailPage({ companyId }: { companyId: number }) {
  const { showToast } = useToast();
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [history, setHistory] = useState<CoverageImport[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [agentsRefresh, setAgentsRefresh] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [profile, imports] = await Promise.all([fetchAdminCompany(companyId), fetchCoverageHistory(companyId)]);
      setCompany(profile);
      setHistory(imports);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Entreprise introuvable.");
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleImport = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      const result = await importCoverageList(companyId, file, deactivateMissing);
      setFile(null);
      setAgentsRefresh((n) => n + 1);
      await load();
      showToast(`${result.created} agent(s) créé(s), ${result.updated} mis à jour.`);
      if (result.errors.length > 0) setError(`${result.errors.length} ligne(s) ignorée(s) : ${result.errors.slice(0, 3).join(" ")}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import impossible.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!company) return;
    const next = company.status === "active" ? "inactive" : "active";
    if (!window.confirm(`Voulez-vous vraiment ${next === "inactive" ? "désactiver" : "activer"} « ${company.name} » ?`)) return;
    setSaving(true);
    try {
      await setAdminCompanyStatus(company.id, next);
      await load();
      showToast("Statut mis à jour.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mise à jour impossible.");
    } finally {
      setSaving(false);
    }
  };

  if (!company) {
    return (
      <>
        <Breadcrumbs items={[{ label: "GESTION" }, { label: "Entreprises", href: "/dashboard/companies" }]} />
        <p className={`text-sm ${error ? "text-red-600" : "text-muted"}`}>{error ?? "Chargement…"}</p>
      </>
    );
  }

  const details: Array<[string, string | null | undefined]> = [
    ["Secteur", company.sector],
    ["RCCM", company.rccm],
    ["Ville", company.city],
    ["Adresse", company.address],
    ["Téléphone", company.phone],
    ["Email", company.email],
    ["Interlocuteur (RH)", [company.contact_name, company.contact_phone, company.contact_email].filter(Boolean).join(" · ")],
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: "GESTION" }, { label: "Entreprises", href: "/dashboard/companies" }, { label: company.name }]} />

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-foreground">
            <Building2 size={20} />
            {company.name}
            <StatusBadge variant={company.status === "active" ? "success" : "neutral"}>
              {company.status === "active" ? "ACTIF" : "INACTIF"}
            </StatusBadge>
          </h1>
          <p className="mt-1 text-sm text-muted">
            {company.agents_count ?? 0} agent(s) · {company.dependants_count ?? 0} ayant(s) droit ·{" "}
            {company.insurer_name ? `assurée par ${company.insurer_name}` : "sans assureur"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/dashboard/companies" className={ghostClass}>
            <ArrowLeft size={14} />
            Retour
          </Link>
          <button type="button" className={ghostClass} disabled={saving} onClick={() => void handleToggle()}>
            <Power size={14} />
            {company.status === "active" ? "Désactiver" : "Activer"}
          </button>
          <Link
            to={`/dashboard/companies/${company.id}/edit`}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
          >
            <Pencil size={14} />
            Modifier
          </Link>
        </div>
      </div>

      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="theme-card p-5">
          <h2 className="mb-3 text-sm font-semibold text-foreground">Fiche entreprise</h2>
          <dl className="flex flex-col gap-2 text-sm">
            {details.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-muted">{label}</dt>
                <dd className="text-right text-foreground">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="theme-card p-5">
          <h2 className="mb-1 text-sm font-semibold text-foreground">Assureur</h2>
          {company.insurer_name ? (
            <p className="mb-3 text-sm text-foreground">
              {company.insurer_name}
            </p>
          ) : (
            <p className="mb-3 text-sm text-amber-700">
              Sans assureur : rattachez-la depuis Assureurs → Entreprises, en choisissant ses packages autorisés.
            </p>
          )}
          {company.insurer_name ? (
            <>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted">Packages autorisés</h3>
              <div className="mb-3 flex flex-wrap gap-1">
                {(company.packages ?? []).length === 0 ? <span className="text-sm text-muted">Aucun</span> : null}
                {(company.packages ?? []).map((pkg) => (
                  <span key={pkg.id} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{pkg.name}</span>
                ))}
              </div>
            </>
          ) : null}
          <h3 className="mb-2 text-xs font-semibold uppercase text-muted">Historique</h3>
          {company.history.length === 0 ? <p className="text-sm text-muted">Aucun rattachement.</p> : null}
          <ul className="flex flex-col gap-1.5 text-sm">
            {company.history.map((item, index) => (
              <li key={`${item.at}-${index}`} className="text-foreground">
                <span className="text-muted">{formatDate(item.at)} · </span>
                {item.from && item.to ? `transférée de ${item.from} vers ${item.to}` : item.to ? `rattachée à ${item.to}` : `retirée de ${item.from ?? "son assureur"}`}
                {item.by ? <span className="text-muted"> ({item.by})</span> : null}
              </li>
            ))}
          </ul>
        </section>

      </div>

      <section className="theme-card mt-6 flex flex-col gap-3 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-foreground">Mise à jour du portefeuille : liste de couverture</h2>
          <button type="button" className={ghostClass} onClick={downloadTemplate}>
            <Download size={14} />
            Télécharger le modèle
          </button>
        </div>
        <p className="text-xs text-muted">
          Fichier CSV avec l'en-tête : matricule ; nom ; date_naissance ; sexe ; telephone ; lien. La colonne « lien » est
          facultative : vide ou « agent » pour l'agent ; « conjoint », « enfant » ou « parent » pour un ayant droit, avec le
          matricule de l'agent dont il dépend. Le portefeuille suit l'entreprise si elle change d'assureur.
        </p>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <input
            id="company-coverage-file"
            type="file"
            accept=".csv,text/csv"
            aria-label="Fichier de la liste de couverture"
            className="text-sm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={deactivateMissing} onChange={(e) => setDeactivateMissing(e.target.checked)} />
            Désactiver les agents absents de cette liste
          </label>
          <button
            type="button"
            disabled={saving || !file}
            onClick={() => void handleImport()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
          >
            <Upload size={15} />
            Importer la liste
          </button>
        </div>
        {history.length > 0 ? (
          <ul className="flex flex-col gap-1 text-xs text-muted">
            {history.map((item) => (
              <li key={item.id}>
                {item.imported_at} · {item.file_name} · {item.created} créés, {item.updated} mis à jour, {item.deactivated} désactivés
                {item.errors.length ? `, ${item.errors.length} erreur(s)` : ""}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <div className="mt-6">
        <AgentsRegistry companyId={company.id} refreshKey={agentsRefresh} allowPhoto />
      </div>
    </>
  );
}
